import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  InstructionGenerationPromptDTO,
  TeamInstructionCoverageBatchResultDTO,
} from "@shaarp/common";
import { FileEditIcon, Loader2Icon, RefreshCwIcon } from "lucide-react";
import { useState } from "react";
import GodBoardWebhookActions from "./god-board-webhook-actions";

export default function AdministrationGodBoardPage() {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [content, setContent] = useState("");
  const [prompt, setPrompt] = useState<InstructionGenerationPromptDTO | null>(
    null
  );

  const fetchPrompt = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<InstructionGenerationPromptDTO | null>(
        "/god-board/teams-instruction/generation-prompt"
      );
      if (data) {
        setPrompt(data);
        setContent(data.content);
      }
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim()) return;
    setIsSaving(true);
    try {
      const { data } = await api.post<InstructionGenerationPromptDTO>(
        "/god-board/teams-instruction/generation-prompt",
        { content }
      );
      setPrompt(data);
      setShowPromptDialog(false);
      toast({
        variant: "default",
        title: t("admin.godBoard.saved"),
      });
    } catch (error) {
      toastError();
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecalculateCoverage = async () => {
    setIsRecalculating(true);
    try {
      const { data } = await api.post<TeamInstructionCoverageBatchResultDTO>(
        "/god-board/teams-instruction/recalculate-coverage"
      );
      toast({
        variant: "default",
        title: t("admin.godBoard.recalculateCoverageSuccess", {
          total: data.total,
          succeeded: data.succeeded,
          failed: data.failed,
        }),
      });
    } catch (error) {
      toastError();
    } finally {
      setIsRecalculating(false);
    }
  };

  const hasChanges = prompt ? content !== prompt.content : content.length > 0;

  const handleOpenPromptDialog = async () => {
    await fetchPrompt();
    if (prompt) {
      setContent(prompt.content);
    }
    setShowPromptDialog(true);
  };

  return (
    <>
      <Dialog open={showPromptDialog} onOpenChange={setShowPromptDialog}>
        <DialogContent className="max-w-[90vw] overflow-auto">
          <DialogHeader>
            <DialogTitle>{t("admin.godBoard.promptTitle")}</DialogTitle>
            <DialogDescription>
              {prompt && prompt.author
                ? t("admin.godBoard.lastUpdatedBy", {
                    author: prompt.author.fullName,
                    date: new Date(prompt.createdAt).toLocaleDateString(),
                  })
                : t("admin.godBoard.promptDialogDescription")}
            </DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 15 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          ) : (
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t("admin.godBoard.placeholder")}
              rows={15}
              className="font-mono text-sm"
            />
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPromptDialog(false)}
            >
              {t("admin.godBoard.promptDialog.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
              {isSaving && (
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("admin.godBoard.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="overflow-auto absolute top-0 left-0 flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-4 w-full h-full">
        <div className="flex-1 p-8 pt-0 space-y-5">
          <div className="flex justify-between">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">
                {t("admin.godBoard.title")}
              </h2>
              <p className="text-muted-foreground">
                {t("admin.godBoard.subtitle")}
              </p>
            </div>
          </div>

          <Separator className="my-6" />

          <div className="space-y-3">
            <h3 className="text-lg font-semibold">
              {t("admin.godBoard.actionsTitle")}
            </h3>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleOpenPromptDialog}>
                <FileEditIcon className="mr-2 h-4 w-4" />
                {t("admin.godBoard.editPrompt")}
              </Button>
              <p className="text-sm text-muted-foreground">
                {t("admin.godBoard.editPromptDescription")}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={isRecalculating}>
                    {isRecalculating ? (
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCwIcon className="mr-2 h-4 w-4" />
                    )}
                    {t("admin.godBoard.recalculateCoverage")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t("admin.godBoard.recalculateCoverageConfirm.title")}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t(
                        "admin.godBoard.recalculateCoverageConfirm.description"
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>
                      {t("admin.godBoard.recalculateCoverageConfirm.cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={handleRecalculateCoverage}>
                      {t("admin.godBoard.recalculateCoverageConfirm.confirm")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <p className="text-sm text-muted-foreground">
                {t("admin.godBoard.recalculateCoverageDescription")}
              </p>
            </div>
          </div>

          <Separator className="my-6" />

          <GodBoardWebhookActions />
        </div>
      </div>
    </>
  );
}
